import { eventTimezones } from "pages/EventsManagement/eventCreationForm"

// The Events Planner's view in the address (B3 pinned decision Q4): filters, view and period, so a link can be shared; the
// last one is kept in browser storage per user, for opening the page from the menu (as the Events list, directoryQuery.js).

export const COUNTRIES = ["Romania", "Bulgaria"]
export const SPORTS = ["tennis", "padel"]
// The states of a proposal the calendar reads by default, and those shown only on request (story 11).
export const OPEN_STATES = ["draft", "proposed", "confirmed"]
export const CLOSED_STATES = ["refused", "withdrawn"]
const LIST_KEYS = ["sport", "category", "organizer", "county", "region", "state"]
const QUEUES = ["drafts", "waiting", "waitingLong", "incomplete", "readyToPublish", "warnings", "checkName"]
// The page's tabs (ticket 16; B3 pinned decision Q1): the Calendar first, then To send and Waiting for answer (Phase C3), and Ready to
// publish (Phase E2p).
export const TABS = ["calendar", "toSend", "waiting", "toPublish"]
// The views of the Calendar tab (ticket 15): Season first, by region or by organizer; Month, comfortable or compact; List.
export const VIEWS = ["season", "month", "list"]
const GROUPS = ["region", "organizer"]
const DENSITIES = ["comfortable", "compact"]
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/

// „Sezonul” (glossary): summer April–October, winter November–March; its view adds a month either side, winter October–April and
// summer March–November. A season is named by the year it starts: winter-2026 is Winter 2026/27.
const SEASON = /^(winter|summer)-(\d{4})$/
export const seasonWindow = season => {
  const [, kind, year] = SEASON.exec(season)
  return kind === "winter" ? { from: `${year}-10-01`, to: `${Number(year) + 1}-05-01` } : { from: `${year}-03-01`, to: `${year}-12-01` }
}
export const seasonLabel = season => {
  const [, kind, year] = SEASON.exec(season)
  return kind === "winter" ? `Winter ${year}/${String(Number(year) + 1).slice(2)}` : `Summer ${year}`
}
export const nextSeason = season => {
  const [, kind, year] = SEASON.exec(season)
  return kind === "winter" ? `summer-${Number(year) + 1}` : `winter-${year}`
}
export const previousSeason = season => {
  const [, kind, year] = SEASON.exec(season)
  return kind === "winter" ? `summer-${year}` : `winter-${Number(year) - 1}`
}
// The season of a day: summer April–October, winter November–March.
export const seasonOfDay = day => (Number(day.slice(5, 7)) >= 4 && Number(day.slice(5, 7)) <= 10 ? "summer" : "winter")
// The season being planned: the next one to start after `today` (Winter 2026/27 on 21 Oct 2026, Summer 2027 from 1 Nov 2026).
export const seasonBeingPlanned = today => {
  const year = Number(today.slice(0, 4))
  if (today < `${year}-04-01`) return `summer-${year}`
  if (today < `${year}-11-01`) return `winter-${year}`
  return `summer-${year + 1}`
}
// The months of a season's view ("2026-10" … "2027-04"), and whether a month is one of the season's own (not a month either side).
export const seasonMonths = season => {
  const { from, to } = seasonWindow(season)
  const months = []
  for (let month = from.slice(0, 7); month < to.slice(0, 7); month = nextMonth(month)) months.push(month)
  return months
}
export const inSeasonProper = (season, month) => {
  const months = seasonMonths(season)
  return month !== months[0] && month !== months[months.length - 1]
}
export const nextMonth = month => {
  const [year, number] = month.split("-").map(Number)
  return number === 12 ? `${year + 1}-01` : `${year}-${String(number + 1).padStart(2, "0")}`
}
export const previousMonth = month => {
  const [year, number] = month.split("-").map(Number)
  return number === 1 ? `${year - 1}-12` : `${year}-${String(number - 1).padStart(2, "0")}`
}
// The season a month belongs to: the one shown when its view holds the month, otherwise the season of the month (the seasons'
// views overlap by a month either side).
export const seasonOfMonth = (month, shown) => {
  if (shown && seasonMonths(shown).includes(month)) return shown
  const year = Number(month.slice(0, 4))
  const kind = seasonOfDay(`${month}-01`)
  return kind === "summer" ? `summer-${year}` : `winter-${Number(month.slice(5, 7)) >= 11 ? year : year - 1}`
}
// The month a season's Month view opens on: today's when the season's view holds it, else its first.
export const monthOpened = (season, today) => (seasonMonths(season).includes(today.slice(0, 7)) ? today.slice(0, 7) : seasonMonths(season)[0])

const listOf = (params, key) => (params.get(key) || "").split(",").filter(Boolean)
export const readPlannerQuery = (search, today) => {
  const params = new URLSearchParams(search)
  const lists = Object.fromEntries(LIST_KEYS.map(key => [key, listOf(params, key)]))
  const sports = lists.sport.filter(sport => SPORTS.includes(sport))
  const states = lists.state.filter(state => [...OPEN_STATES, ...CLOSED_STATES].includes(state))
  const season = SEASON.test(params.get("season") || "") ? params.get("season") : seasonBeingPlanned(today)
  const view = VIEWS.includes(params.get("view")) ? params.get("view") : VIEWS[0]
  const month = MONTH.test(params.get("month") || "") && seasonMonths(season).includes(params.get("month")) ? params.get("month") : monthOpened(season, today)
  return {
    country: COUNTRIES.includes(params.get("country")) ? params.get("country") : COUNTRIES[0],
    season,
    sports: sports.length ? sports : ["tennis"],
    categories: lists.category, organizers: lists.organizer, counties: lists.county, regions: lists.region,
    states: states.length ? states : OPEN_STATES,
    published: params.get("published") !== "0",
    warnings: params.get("warnings") === "1",
    queue: QUEUES.includes(params.get("queue")) ? params.get("queue") : null,
    tab: TABS.includes(params.get("tab")) ? params.get("tab") : TABS[0],
    view,
    group: GROUPS.includes(params.get("group")) ? params.get("group") : GROUPS[0],
    month,
    density: DENSITIES.includes(params.get("density")) ? params.get("density") : DENSITIES[0],
  }
}
// The defaults are left out: the first opening's address reads "?country=Romania&season=winter-2026&sport=tennis". The month is kept
// only in the Month view.
export const writePlannerQuery = query => {
  const params = new URLSearchParams({ country: query.country, season: query.season, sport: query.sports.join(",") })
  const lists = { category: query.categories, organizer: query.organizers, county: query.counties, region: query.regions }
  Object.entries(lists).forEach(([key, values]) => { if (values.length) params.set(key, values.join(",")) })
  if (query.states.join(",") !== OPEN_STATES.join(",")) params.set("state", query.states.join(","))
  if (!query.published) params.set("published", "0")
  if (query.warnings) params.set("warnings", "1")
  if (query.view && query.view !== VIEWS[0]) params.set("view", query.view)
  if (query.view === "season" && query.group && query.group !== GROUPS[0]) params.set("group", query.group)
  if (query.view === "month" && query.month) params.set("month", query.month)
  if (query.view === "month" && query.density && query.density !== DENSITIES[0]) params.set("density", query.density)
  if (query.queue) params.set("queue", query.queue)
  if (query.tab && query.tab !== TABS[0]) params.set("tab", query.tab)
  return "?" + params
}

// The queues of sending and answers (Phase C2a) read one country at a time. To send offers the new drafts of a period: a season by
// its own months (winter November–March, summer April–October), or every upcoming date from today.
export const sendPeriodWindow = (period, season, today) => {
  if (period === "upcoming") return { from: today }
  const [kind, year] = season.split("-")
  return kind === "winter" ? { from: `${year}-11-01`, to: `${Number(year) + 1}-04-01` } : { from: `${year}-04-01`, to: `${year}-11-01` }
}
export const toSendPath = (query, period, today) => "/admin/tournament-proposal-queues/to-send?"
  + new URLSearchParams({ country: query.country, ...sendPeriodWindow(period, query.season, today) })
export const waitingPath = query => "/admin/tournament-proposal-queues/waiting?" + new URLSearchParams({ country: query.country })
// Ready to publish (Phase E1p): the country's Confirmată proposals with their publication preview, or those a review names.
export const toPublishPath = (country, ids) => "/admin/tournament-proposal-queues/to-publish?"
  + new URLSearchParams({ country, ...(ids && { proposals: ids.join(",") }) })
// One organizer's list as a sending would carry it, with the drafts it would add (Phase C2b).
export const organizerListPath = (organizerId, drafts) => `/admin/tournament-proposal-organizers/${organizerId}/list`
  + (drafts.length ? "?" + new URLSearchParams({ drafts: drafts.join(",") }) : "")

// The calendar read of a view: the country, the season's days, the sports, the Sportya circuit (Tenis10 is not selectable yet)
// and the states. Category, organizer, county and region filter the answer in the page, so their choices keep their counts.
export const calendarReadPath = query => {
  const { from, to } = seasonWindow(query.season)
  const params = new URLSearchParams({ country: query.country, from, to, sportType: query.sports.join(","), circuit: "sportya" })
  if (query.states.join(",") !== OPEN_STATES.join(",")) params.set("states", query.states.join(","))
  return "/admin/competitive-calendar?" + params
}
export const countryTimezone = country => eventTimezones[country]

const storageKey = userId => "sportya-events-planner:" + userId
export const rememberPlannerSearch = (userId, search) => { try { localStorage.setItem(storageKey(userId), search) } catch (error) { /* Storage is optional. */ } }
export const rememberedPlannerSearch = userId => {
  try { return localStorage.getItem(storageKey(userId)) || "" } catch (error) { return "" }
}
